import * as React from 'react';
import { FilterOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Select, Button, Popover } from 'antd';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { getTimeZoneFieldName, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { useEffect, useRef, useState } from 'react';
import ClearFilter from '../clear_filters/ClearFilters';
import { Sorter } from '../WorkOrderTable/sorters/Sorters';
import { isEqual } from 'lodash';
import { FilterOptionsWrapper } from '../filter_option_wrapper/FilterOptionWrapper';
import { FILTER_OPTIONS } from '../../utils/FilterUtils';
const queryString = require('qs');

export function AdvancedFilters(props) {
	const {
		filtersTargetCollectionName,
		clearAndUpdateFilters,
		filtersStateSlice,
		filterConfig,
		filterPlaceHolder,
		hideClearFilters,
		updateFilters,
		applySorting,
		sorters,
		sorterItems = [],
		inputParamName = 'search',
		initialFilters = {}, //The initial states of the filters
		preAppliedFilters = {}, //Filters that are on by default and remain after a clear
		showSearch = true,
		moveClearToEnd = false,
		initialPagination,
		defaultSorters,
	} = props;

	const filters = filtersStateSlice.hasOwnProperty(filtersTargetCollectionName)
		? nullSafeGet(`${filtersTargetCollectionName}.filters`, filtersStateSlice)
		: filtersStateSlice;

	//Filters which change, but have specific starting states that are not undefined
	const defaultFilters = {};
	filterConfig
		.filter((fc) => fc.hasOwnProperty('defaultValue') && fc.hasOwnProperty('fieldName'))
		.filter(
			(fc) =>
				fc.hasOwnProperty('additionalDefaultValues') &&
				Object.keys(nullSafeGetOrElse(`additionalDefaultValues`, fc, {})).map(
					(key) => (defaultFilters[key] = nullSafeGet(`additionalDefaultValues.${key}`, fc))
				)
		)
		.map((fc) => (defaultFilters[fc.fieldName] = fc['defaultValue']));

	const initialSearch = queryString.parse(window.location.search, { ignoreQueryPrefix: true })[
		inputParamName
	];

	const [search, setSearch] = useState(initialSearch);

	const timer = useRef(undefined);
	useEffect(() => {
		clearTimeout(timer.current);
		timer.current =
			timer.current === undefined
				? null
				: setTimeout(() => {
						if (search != initialSearch) {
							updateFilters(
								{ [inputParamName]: search && search.trim() },
								filtersTargetCollectionName
							);
						}
				  }, 400);
	}, [search]);

	const onChange = (e) => {
		setSearch(e.target.value.length > 0 ? e.target.value : undefined);
	};
	const clearFilters = (e) => {
		setSearch(null);
		clearAndUpdateFilters(
			{ ...preAppliedFilters, ...defaultFilters, [inputParamName]: undefined },
			filtersTargetCollectionName
		);
		defaultSorters && applySorting && applySorting(defaultSorters);
	};

	const isActive = filterConfig
		.filter((filter) => !filter.showOutsideDropdown)
		.some((fc) => {
			const optionsToCheck =
				fc.filterOptions && fc.filterOptions.length > 0
					? fc.filterOptions.map((o) => o.value)
					: [undefined];

			return optionsToCheck.some((option) => {
				let value = fc.filtersValueAccessor(filtersStateSlice, option);
				value =
					value === 'true'
						? true
						: value === 'false'
						? false
						: value === 'undefined'
						? undefined
						: value;
				return Array.isArray(value)
					? value.length > 0 && value.filter((_) => !!_).length > 0
					: Object.keys(defaultFilters).includes(fc.fieldName)
					? !isEqual(defaultFilters[fc.fieldName], value)
					: value;
			});
		});

	const buildFilter = (filter) => {
		const ComponentToRender = filter.component || OWAsyncSelect;
		const renderMethod = (entity) => (
			<Select.Option key={filter.keyAccessor(entity)} value={filter.valueAccessor(entity)}>
				{filter.renderItem(entity)}
			</Select.Option>
		);
		return (
			<ComponentToRender
				allowClear={typeof filter.allowClear === 'boolean' ? filter.allowClear : true}
				placeholder={filter.placeholder}
				style={filter.style || { width: '100%' }}
				stateSlice={filter.stateSlice}
				value={filter.filtersValueAccessor(
					filtersStateSlice,
					filter.selectOption || FILTER_OPTIONS.IS
				)}
				valueAccessor={filter.valueAccessor}
				labelAccessor={filter.labelAccessor}
				onChange={(val, ...rest) => {
					if (filter.handleChange) {
						filter.handleChange(val, ...rest);
					}
				}}
				targetCollectionName={filter.targetCollectionName}
				additionalFilters={
					filter.additionalFiltersAccessor
						? filter.additionalFiltersAccessor(filtersStateSlice)
						: {}
				}
				items={filter.items}
				tagRender={filter.tagRender || undefined}
				fetchData={(
					searchText,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				) =>
					filter.fetch(
						{ name: searchText },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					)
				}
				fetchMultiple={
					filter.fetchMultiple ? (...args) => filter.fetchMultiple(...args) : undefined
				}
				renderRecord={filter.renderRecord || renderMethod}
				sortBy={filter.sortBy}
				mode={filter.mode}
				info={filter.info}
				initialPagination={initialPagination}
				label={filter.label}
				showTimeZone={filter.showTimeZone}
				treeSelectProps={filter.treeSelectProps}
			/>
		);
	};

	const showFiltersUI =
		filterConfig
			.filter((filter) => !filter.hideFilter || (filter.hideFilter && filter.elementDefined))
			.filter((filter) => !filter.showOutsideDropdown).length > 0 || props.children;

	const filtersUI = filterConfig
		.filter((filter) => !filter.hideFilter)
		.filter((filter) => !filter.showOutsideDropdown)
		.map((filter) => {
			const filterComponent = buildFilter(filter);

			if (filter.isSpecial && filterComponent) {
				if (filter.filterOptions && filter.filterOptions.length > 0) {
					return (
						<FilterOptionsWrapper
							filter={filter}
							buildFilter={buildFilter}
							filtersStateSlice={filtersStateSlice}
							updateFilters={updateFilters}
						/>
					);
				}
				return filterComponent;
			}

			return (
				filterComponent && (
					<Form.Item label={filter.filterOptions ? null : filter.label} key={filter.label}>
						<FilterOptionsWrapper
							filter={filter}
							buildFilter={buildFilter}
							filtersStateSlice={filtersStateSlice}
							updateFilters={updateFilters}
						/>
					</Form.Item>
				)
			);
		});

	const outsideFiltersUI = filterConfig
		.filter((filter) => !filter.hideFilter)
		.filter((filter) => filter.showOutsideDropdown)
		.map((filter) =>
			buildFilter({
				...filter,
				handleChange:
					filter.handleChange && filter.filterOptions && filter.filterOptions.length > 0
						? filter.handleChange(FILTER_OPTIONS.IS)
						: filter.handleChange,
			})
		);

	const popoverContent = (
		<Form
			layout="vertical"
			style={{ width: 440, maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden' }}
		>
			<h5>Additional Filters</h5>
			{props.children}
			{filtersUI}
		</Form>
	);

	return (
		<div style={{ display: 'inline-flex', flexDirection: 'row', gap: '8px' }}>
			{showSearch ? (
				<Input.Search
					placeholder={filterPlaceHolder || 'Search'}
					value={search}
					style={{ width: 180 }}
					onChange={onChange}
				/>
			) : null}
			{showFiltersUI ? (
				<Popover content={popoverContent} trigger="click">
					<Button type={isActive ? 'primary' : undefined} size="large" icon={<FilterOutlined />}>
						<span className="inline-block-visible-xxl">Filters</span>
					</Button>
				</Popover>
			) : null}
			{sorterItems.length > 0 ? (
				<Sorter value={sorters} onChange={applySorting} items={sorterItems} />
			) : null}
			{outsideFiltersUI.length > 0 ? outsideFiltersUI.map((filter) => filter) : null}
			{!hideClearFilters && (
				<ClearFilter
					defaultFilters={defaultFilters}
					preAppliedFilters={preAppliedFilters}
					activeFilters={filters}
					activeSorter={sorters}
					defaultSorters={defaultSorters}
					clearFilters={clearFilters}
					style={moveClearToEnd ? { order: 100 } : {}}
				/>
			)}
		</div>
	);
}
AdvancedFilters.contextType = AdvancedFilters;
